import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, SlidersHorizontal, X } from "lucide-react";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [qualities, setQualities] = useState([]);
  const [q, setQ] = useState("");
  const [quality, setQuality] = useState("all");
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    api.get("/dealer/qualities").then(({ data }) => setQualities(data));
  }, []);

  useEffect(() => {
    const params = {};
    if (q) params.q = q;
    if (quality !== "all") params.quality_id = quality;
    const t = setTimeout(() => {
      api.get("/dealer/products", { params }).then(({ data }) => setProducts(data));
    }, 250);
    return () => clearTimeout(t);
  }, [q, quality]);

  const selectedQ = qualities.find((x) => x.id === quality);

  const Sidebar = (
    <div className="bg-white border border-[#eae5dc] p-5">
      <div className="flex items-center justify-between mb-4">
        <span className="label-tag">Kaliteler</span>
        <button className="lg:hidden p-1" onClick={() => setFilterOpen(false)} aria-label="Kapat">
          <X size={18} strokeWidth={1.5} />
        </button>
      </div>
      <button
        onClick={() => { setQuality("all"); setFilterOpen(false); }}
        className={`w-full text-left px-3 py-2.5 text-sm border-l-2 transition-colors ${
          quality === "all"
            ? "bg-[#f7f5f0] border-l-[#c8a97e] text-[#1a1a1a] font-medium"
            : "border-l-transparent hover:bg-[#f7f5f0] text-[#3d3a35]"
        }`}
        data-testid="dealer-quality-filter-all"
      >
        Tümü <span className="float-right text-xs text-[#6b655c]">{products.length}</span>
      </button>
      {qualities.map((qq) => (
        <button
          key={qq.id}
          onClick={() => { setQuality(qq.id); setFilterOpen(false); }}
          className={`w-full text-left px-3 py-2.5 text-sm border-l-2 transition-colors ${
            quality === qq.id
              ? "bg-[#f7f5f0] border-l-[#c8a97e] text-[#1a1a1a] font-medium"
              : "border-l-transparent hover:bg-[#f7f5f0] text-[#3d3a35]"
          }`}
          data-testid={`dealer-quality-filter-${qq.id}`}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="truncate">{qq.name}</span>
            <span className="text-xs text-[#6b655c] flex-shrink-0">{qq.product_count}</span>
          </div>
        </button>
      ))}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="dealer-products-page">
      <div>
        <span className="label-tag">Katalog</span>
        <h1 className="font-serif text-3xl md:text-4xl mt-2">
          {selectedQ ? selectedQ.name : "Tüm Ürünler"}
        </h1>
        <p className="text-[#6b655c] mt-1 text-sm">
          {selectedQ ? selectedQ.short_description || "Bu kalitedeki desenler" : "Sipariş vermek için ürün seçin"}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Mobile filter button + search */}
        <div className="lg:hidden flex gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6b655c]" />
            <Input placeholder="Ürün adı" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="dealer-product-search" />
          </div>
          <button
            onClick={() => setFilterOpen(true)}
            className="px-4 py-2 border border-[#eae5dc] bg-white text-sm flex items-center gap-2"
            data-testid="dealer-filter-open-btn"
          >
            <SlidersHorizontal size={14} /> Filtre
          </button>
        </div>

        {/* Desktop sidebar */}
        <aside className="hidden lg:block sticky top-4 self-start">
          {Sidebar}
        </aside>

        {/* Mobile drawer */}
        <div className={`lg:hidden fixed inset-0 z-50 ${filterOpen ? "pointer-events-auto" : "pointer-events-none"}`}>
          <div
            className={`absolute inset-0 bg-black/50 transition-opacity ${filterOpen ? "opacity-100" : "opacity-0"}`}
            onClick={() => setFilterOpen(false)}
          />
          <div className={`absolute right-0 inset-y-0 w-80 max-w-[85vw] bg-[#f7f5f0] overflow-y-auto transition-transform ${filterOpen ? "translate-x-0" : "translate-x-full"} duration-300 p-4`}>
            {Sidebar}
          </div>
        </div>

        <div className="space-y-4">
          {/* Desktop search */}
          <div className="hidden lg:flex bg-white border border-[#eae5dc] p-4">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6b655c]" />
              <Input placeholder="Ürün adı" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {products.length === 0 && <p className="col-span-full text-[#6b655c] py-12 text-center bg-white border border-[#eae5dc]">Ürün bulunamadı.</p>}
            {products.map((p) => (
              <Link
                to={`/bayi/urunler/${p.id}`}
                key={p.id}
                className="group bg-white border border-[#eae5dc] hover:border-[#c8a97e] transition-colors overflow-hidden"
                data-testid={`dealer-product-card-${p.id}`}
              >
                <div className="aspect-[4/5] bg-[#eae5dc] overflow-hidden relative">
                  {p.cover_image ? (
                    <img src={p.cover_image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#6b655c]">Görsel yok</div>
                  )}
                  {p.quality_name && (
                    <span className="absolute top-3 left-3 bg-[#1a1a1a]/85 text-[#f7f5f0] text-[0.65rem] tracking-[0.2em] uppercase px-2 py-1">{p.quality_name}</span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-serif text-lg leading-tight">{p.name}</h3>
                  <div className="flex items-center justify-between mt-3 text-xs">
                    <span className="text-[#6b655c]">{p.variants_count} varyant</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
